<script setup lang="ts">
import { shallowRef } from "vue"

import { ICON } from "@/lib/icons"

defineProps<{
  canCapture: boolean
  canStartNext: boolean
  canFinish: boolean
  canRetryRecovery: boolean
  canReplaceStaleQueue: boolean
  canDiscardStaleQueue: boolean
  busy: boolean
}>()

const emit = defineEmits<{
  capture: []
  startNext: []
  finish: []
  retryRecovery: []
  replaceStaleQueue: []
  discardStaleQueue: []
}>()

const confirmingDiscard = shallowRef(false)

function requestDiscard(): void {
  confirmingDiscard.value = true
}

function keepPhotos(): void {
  confirmingDiscard.value = false
}

function confirmDiscard(): void {
  confirmingDiscard.value = false
  emit("discardStaleQueue")
}
</script>

<template>
  <div
    class="flex w-full flex-col gap-4 px-4 pt-8"
    :style="{ paddingBottom: 'max(1rem, var(--safe-bottom))' }"
  >
    <div
      v-if="canReplaceStaleQueue || canDiscardStaleQueue"
      class="mx-auto flex w-full max-w-sm flex-col gap-2"
    >
      <UButton
        v-if="canReplaceStaleQueue"
        data-testid="replace-stale-queue"
        block
        size="xl"
        color="primary"
        :icon="ICON.rotate"
        @click="$emit('replaceStaleQueue')"
      >
        Start new batch with saved photos
      </UButton>
      <UButton
        v-if="canDiscardStaleQueue && !confirmingDiscard"
        data-testid="discard-stale-queue"
        block
        size="xl"
        color="neutral"
        variant="outline"
        :icon="ICON.delete"
        @click="requestDiscard"
      >
        Discard saved photos
      </UButton>
      <div v-else-if="canDiscardStaleQueue" class="flex flex-col gap-2 rounded-xl bg-black/80 p-3">
        <p class="text-center text-sm text-white">Discard these saved photos permanently?</p>
        <div class="grid grid-cols-2 gap-2">
          <UButton color="neutral" variant="outline" @click="keepPhotos"> Keep photos </UButton>
          <UButton data-testid="confirm-discard-stale-queue" color="error" @click="confirmDiscard">
            Discard saved photos
          </UButton>
        </div>
      </div>
    </div>

    <div v-else class="flex items-center justify-center">
      <button
        type="button"
        aria-label="Take photo"
        :disabled="!canCapture"
        class="flex size-20 items-center justify-center rounded-full border-4 border-white bg-white/20 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
        @click="$emit('capture')"
      >
        <span class="size-14 rounded-full bg-white" />
      </button>
    </div>

    <UButton
      v-if="canRetryRecovery && !canReplaceStaleQueue && !canDiscardStaleQueue"
      data-testid="retry-recovery"
      block
      size="xl"
      color="warning"
      :icon="ICON.rotate"
      @click="$emit('retryRecovery')"
    >
      Retry recovery
    </UButton>

    <div v-if="!canReplaceStaleQueue && !canDiscardStaleQueue" class="grid grid-cols-2 gap-2">
      <UButton
        data-testid="next-item"
        block
        size="xl"
        color="neutral"
        variant="solid"
        :icon="ICON.nextItem"
        :disabled="!canStartNext || busy"
        @click="$emit('startNext')"
      >
        Next item
      </UButton>
      <UButton
        data-testid="finish-batch"
        block
        size="xl"
        color="primary"
        variant="solid"
        :icon="ICON.check"
        :disabled="!canFinish || busy"
        @click="$emit('finish')"
      >
        Finish batch
      </UButton>
    </div>
  </div>
</template>
